import { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { api, API, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, Upload } from "lucide-react";
import { toast } from "sonner";


export default function OutletSettings() {
  const { id } = useParams();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    (async () => {
      const { data } = await api.get(`/outlets/${id}`);
      setForm(data);
    })();
  }, [id]);

  const update = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = {
        name: form.name, logo_url: form.logo_url, address: form.address,
        google_review_url: form.google_review_url, whatsapp_number: form.whatsapp_number,
        brand_color: form.brand_color, thank_you_message: form.thank_you_message,
      };
      const { data } = await api.patch(`/outlets/${id}`, body);
      setForm(data);
      toast.success("Pengaturan disimpan");
    } catch (e2) {
      toast.error(formatApiError(e2.response?.data?.detail) || "Gagal menyimpan");
    } finally { setSaving(false); }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      update("logo_url", data.url);
      toast.success("Logo diunggah");
    } catch (e2) {
      toast.error(formatApiError(e2.response?.data?.detail) || "Gagal upload");
    } finally { setUploading(false); }
  };

  if (!form) return <div className="text-sm text-zinc-500">Memuat...</div>;

  return (
    <div className="space-y-6 max-w-3xl">
      <Link to="/admin/outlets" className="inline-flex items-center gap-1 text-sm text-zinc-500 hover:text-emerald-700" data-testid="back-outlets-link">
        <ArrowLeft size={14} /> Kembali ke Outlet
      </Link>

      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Pengaturan Outlet</h1>
        <p className="text-sm text-zinc-500 mt-1">Sesuaikan tampilan halaman feedback pelanggan.</p>
      </div>

      <form onSubmit={save} className="bg-white border border-zinc-200 rounded-2xl p-6 space-y-5">
        <div className="flex items-center gap-4">
          {form.logo_url
            ? <img src={form.logo_url} alt="logo" className="w-20 h-20 rounded-xl object-cover border border-zinc-200" />
            : <div className="w-20 h-20 rounded-xl bg-zinc-100 grid place-items-center text-zinc-400 text-xs">Logo</div>}
          <div>
            <input ref={fileRef} type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="logo-upload-input" />
            <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="logo-upload-button">
              <Upload size={14} className="mr-1.5" /> {uploading ? "Mengunggah..." : "Unggah Logo"}
            </Button>
            <div className="text-xs text-zinc-500 mt-2">PNG/JPG, maks 5MB.</div>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="name">Nama Bisnis</Label>
            <Input id="name" value={form.name || ""} onChange={e => update("name", e.target.value)} data-testid="setting-name-input" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="brand_color">Warna Utama Brand</Label>
            <div className="flex gap-2">
              <Input id="brand_color" type="color" value={form.brand_color || "#059669"} onChange={e => update("brand_color", e.target.value)}
                className="w-16 p-1 h-10 cursor-pointer" data-testid="setting-brand-color-input" />
              <Input value={form.brand_color || ""} onChange={e => update("brand_color", e.target.value)} className="flex-1" />
            </div>
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="address">Alamat</Label>
          <Input id="address" value={form.address || ""} onChange={e => update("address", e.target.value)} data-testid="setting-address-input" />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="google_review_url">Google Review URL</Label>
          <Input id="google_review_url" placeholder="https://g.page/r/..." value={form.google_review_url || ""} onChange={e => update("google_review_url", e.target.value)} data-testid="setting-google-review-input" />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="whatsapp_number">Nomor WhatsApp Bisnis</Label>
          <Input id="whatsapp_number" placeholder="628xxxxxxxxx" value={form.whatsapp_number || ""} onChange={e => update("whatsapp_number", e.target.value)} data-testid="setting-whatsapp-input" />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="thank_you_message">Pesan Terima Kasih</Label>
          <Textarea id="thank_you_message" rows={2} value={form.thank_you_message || ""} onChange={e => update("thank_you_message", e.target.value)} data-testid="setting-thankyou-input" />
        </div>

        <div className="flex gap-2 pt-2">
          <Button type="submit" disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-outlet-button">
            {saving ? "Menyimpan..." : "Simpan Pengaturan"}
          </Button>
          <a href={`/f/${form.slug}`} target="_blank" rel="noreferrer">
            <Button type="button" variant="outline">Pratinjau</Button>
          </a>
        </div>
      </form>
    </div>
  );
}
